import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { User, Calendar, FileText, ChevronRight, Music, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import PortalLayout from './PortalLayout';
import api from '@/lib/axios';
import { formatDate, formatScheduleLabel, courseDisplayName } from '@/lib/utils';
import { statusLabel } from '@/lib/deLabels';
import { remainingHoursLabel, remainingHoursLabelFromTotals } from '@/lib/prepaidCards';
import { getPortalToken } from './portalStorage';

export default function PortalChildren() {
  const [children, setChildren] = useState([]);
  const [enrollments, setEnrollments] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedChild, setSelectedChild] = useState(null);

  useEffect(() => {
    fetchChildren();
  }, []);

  const fetchChildren = async () => {
    try {
      const token = getPortalToken();
      if (token) {
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await api.get('/portal/children');
      setChildren(response.data);
      
      // Auto-select first child
      if (response.data.length > 0) {
        setSelectedChild(response.data[0].id);
        fetchEnrollments(response.data[0].id);
      }
    } catch (error) {
      console.error('Error loading children:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const fetchEnrollments = async (studentId) => {
    try {
      const response = await api.get(`/portal/children/${studentId}/enrollments`);
      setEnrollments(prev => ({ ...prev, [studentId]: response.data }));
    } catch (error) {
      console.error('Error loading enrollments:', error);
    }
  };

  const handleSelectChild = (childId) => {
    setSelectedChild(childId);
    if (!enrollments[childId]) {
      fetchEnrollments(childId);
    }
  };

  if (loading) {
    return (
      <PortalLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-red"></div>
        </div>
      </PortalLayout>
    );
  }

  const selectedChildData = children.find(c => c.id === selectedChild);
  const selectedEnrollments = enrollments[selectedChild] || [];

  return (
    <PortalLayout>
      <div className="space-y-6" data-testid="portal-children">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Meine Kinder</h1>
          <p className="text-slate-600">Übersicht über alle angemeldeten Kinder</p>
        </div>

        {children.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-slate-500">
              Keine Kinder gefunden
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Children List */}
            <div className="space-y-3">
              {children.map((child) => (
                <Card 
                  key={child.id}
                  className={`cursor-pointer transition-all ${
                    selectedChild === child.id 
                      ? 'ring-2 ring-brand-red bg-brand-red/5' 
                      : 'hover:bg-slate-50'
                  }`}
                  onClick={() => handleSelectChild(child.id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-brand-red/10 flex items-center justify-center">
                        <User className="w-6 h-6 text-brand-red" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">
                          {child.first_name} {child.last_name}
                        </p>
                        {child.prepaid_remaining_units > 0 && (
                          <p className="text-xs text-emerald-700 mt-0.5">
                            {remainingHoursLabelFromTotals(child.prepaid_remaining_units, child.prepaid_remaining_minutes)}
                          </p>
                        )}
                        {child.birth_date && (
                          <p className="text-sm text-slate-500">
                            Geb. {formatDate(child.birth_date)}
                          </p>
                        )}
                      </div>
                      <Badge className={
                        child.status === 'active' 
                          ? 'bg-emerald-50 text-emerald-700' 
                          : 'bg-slate-100'
                      }>
                        {statusLabel(child.status)}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Selected Child Details */}
            <div className="lg:col-span-2 space-y-4">
              {selectedChildData && (
                <>
                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-4">
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <Music className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-sm text-slate-500">Kursbelegungen</p>
                            <p className="text-xl font-bold">{selectedChildData.enrollments_count}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-brand-red/10 rounded-lg">
                            <FileText className="w-5 h-5 text-brand-red" />
                          </div>
                          <div>
                            <p className="text-sm text-slate-500">Aktive Verträge</p>
                            <p className="text-xl font-bold">{selectedChildData.active_contracts_count}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="col-span-2" data-testid="portal-child-prepaid">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-emerald-100 rounded-lg">
                            <CreditCard className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm text-slate-500">Karten übrig</p>
                            <p className="text-xl font-bold" data-testid="portal-child-prepaid-remaining">
                              {remainingHoursLabelFromTotals(
                                selectedChildData.prepaid_remaining_units,
                                selectedChildData.prepaid_remaining_minutes,
                              )}
                            </p>
                            {(selectedChildData.prepaid_cards || []).length > 0 && (
                              <ul className="mt-2 space-y-1">
                                {selectedChildData.prepaid_cards.map((card) => (
                                  <li key={card.id} className="text-sm text-slate-600">
                                    {card.name}: {remainingHoursLabel(card.remaining_units, card.unit_duration_minutes)}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Enrollments */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Kursbelegungen</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {selectedEnrollments.length === 0 ? (
                        <p className="text-slate-500 text-sm">Keine aktiven Kursbelegungen</p>
                      ) : (
                        <div className="space-y-3">
                          {selectedEnrollments.map((enrollment) => (
                            <div 
                              key={enrollment.id}
                              className="p-4 border border-slate-200 rounded-lg"
                            >
                              <div className="flex items-start justify-between">
                                <div>
                                  <p className="font-medium text-slate-900">{courseDisplayName(enrollment.course_name, enrollment.subject_name)}</p>
                                  <div className="mt-1 space-y-1 text-sm text-slate-500">
                                    {enrollment.teacher_name && (
                                      <p>Lehrer: {enrollment.teacher_name}</p>
                                    )}
                                    {enrollment.subject_name && (
                                      <p>Fach: {enrollment.subject_name}</p>
                                    )}
                                    {(enrollment.day_of_week || enrollment.time_slot) && (
                                      <p className="flex items-center gap-1">
                                        <Calendar className="w-3 h-3" />
                                        {formatScheduleLabel([enrollment.day_of_week, enrollment.time_slot].filter(Boolean).join(' '))}
                                      </p>
                                    )}
                                    {enrollment.room_name && (
                                      <p>Raum: {enrollment.room_name}</p>
                                    )}
                                  </div>
                                </div>
                                <Badge className={
                                  enrollment.status === 'active' 
                                    ? 'bg-emerald-50 text-emerald-700' 
                                    : 'bg-slate-100'
                                }>
                                  {statusLabel(enrollment.status)}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </PortalLayout>
  );
}
